import { memo, useMemo } from "react";
import cx from "classnames";
import { shallow } from "zustand/shallow";
import { IonLabel } from "@ionic/react";
import { WarningRedIcon } from "../../../../../../../assets/icons";
import { WLButton } from "../../../../../../../../Common/components/WLButton/WLButton";
import { formatNameCapitalize } from "../../../../../../../../Common/utils/common";
import { useWebUserStore } from "../../../../../../../CommonStores";
import PaymentDrawTonightInterfaceStore from "../../../interface/PaymentDrawTonightInterface";
import s from "./Details.module.scss";

const Details: React.FC = (): JSX.Element => {
  const [isMobile, user, totalEntries, selectedProduct, onCreateFixPayment] =
    PaymentDrawTonightInterfaceStore((state) => [
      state.isMobile,
      state.user,
      state.totalEntries,
      state.selectedProduct,
      state.onCreateFixPayment,
    ]);

  const [signInOtpFirstName] = useWebUserStore(
    (state) => [state.signInOtpFirstName],
    shallow,
  );

  const greetingFirstName = useMemo(() => {
    const rawGreetingName =
      user?.firstName?.trim() || signInOtpFirstName?.trim() || "";
    return formatNameCapitalize(rawGreetingName) || "there";
  }, [signInOtpFirstName, user?.firstName]);

  const canFixPayment = Boolean(user && selectedProduct !== undefined);
  const titleNameClass = `${isMobile ? "win-header-2" : "win-headline-1"} extra-bold winnersans secondary-400`;
  const titleClass = `${isMobile ? "win-header-4" : "win-header-1"} extra-bold winnersans secondary-400`;
  const subtitleClass = `${isMobile ? "win-body" : "win-header-4"} medium gilroy white`;
  const subtitleBoldClass = `${isMobile ? "win-body" : "win-header-4"} bold gilroy white`;

  const createFixPaymentButton = useMemo(() => {
    return (
      <WLButton
        onClick={onCreateFixPayment}
        disabled={!canFixPayment}
        className="red-submit-button win-body ion-no-margin full-width"
        isFormComponent
      >
        Fix my payment
      </WLButton>
    );
  }, [canFixPayment, onCreateFixPayment]);

  const createEntriesRemovedContainer = useMemo(() => {
    return (
      <div className={s.entriesRemovedContainer}>
        <div className={s.entriesRemovedTitle}>
          <IonLabel className="win-headline-3 medium winnersans secondary-500">
            {totalEntries}
          </IonLabel>
          <IonLabel className="win-sub medium winnersans tertiary-400-v2 ion-text-start">
            entries have been
            <br />
            removed from this draw
          </IonLabel>
        </div>

        <IonLabel className="win-body medium gilroy tertiary-300-v2 ion-text-center">
          If your payment isn't fixed before the draw, these entries won't
          count.
        </IonLabel>
      </div>
    );
  }, [totalEntries]);

  return (
    <div
      className={cx(s.details, {
        [s.detailsIsMobile]: isMobile,
      })}
    >
      <div className={s.detailsContent}>
        <WarningRedIcon className={s.warningIcon} />
        <IonLabel className={titleClass}>
          <IonLabel className={titleNameClass}>Hey {greetingFirstName}</IonLabel>
          <br />
          You're not in <br />
          tonight's draw
        </IonLabel>
        <IonLabel className={subtitleClass}>
          {`Your last payment didn't go through. `}
          <br />
          You are{" "}
          <IonLabel className={subtitleBoldClass}>
            not currently in the draw.
          </IonLabel>
          <br />
          {` Fix your payment to remain in the running.`}
        </IonLabel>
      </div>

      {!isMobile ? (
        <>
          {createFixPaymentButton}
          {createEntriesRemovedContainer}
        </>
      ) : (
        <>
          {createEntriesRemovedContainer}
          {createFixPaymentButton}
        </>
      )}
    </div>
  );
};

export default memo(Details);
